import "../styles/evKit.css";
import { BalanceData, ChestBand, EventReward, OfferItem, RARITY_IDS, bandWeightsAt } from "@highmarch/domain";
import { closeBtnHtml, fmtCompact, ribbonHtml } from "../kit";
import { icon } from "../iconsKit";
import { wellHtml } from "../components";
import { resourceIconUrl, uiUrl } from "../portraits";
import { essenceIconHtml, sigilIconHtml } from "../forgeArt";
import { supplyIconHtml, supplyName } from "../supplies/supplyArt";
import { oddsHtml, showItemFocus } from "../deals/pickSheet";
import { SHOP_SKIN } from "../shopSkin";
import { fmtSpeedUp } from "./ordersSheet";
import { emblemGlyph, emblemName, gemGlyph, marksGlyph, rewardArt, rewardWords, trophiesGlyph } from "./eventRewards";
import { CHEST_LABEL } from "../forge/forgeWords";

/**
 * The Events tab's look kit (UI pass 2026-10-21, after the Shop's: docs/SHOP.md §19–§23): the bright 96 px banner each
 * mode opens on (a rendered daylight scene with Captain Bram, art/interface/banners/banner_ev*.webp), reward tiles on
 * their rarity squares with the item's name under them (equal heights, the foot strip aligned along the bottom), the red
 * dot on anything to claim, and the item focus a tap on any reward opens (the Shop's showItemFocus for a lone chest,
 * gems or speed-up; a parts list with the chest's odds for a mixed reward). Presentation only.
 */

/** The rendered banner scenes of the Events tab's modes. */
export type EvScene = "event" | "evdaily" | "evendless" | "evchallenge" | "evfree" | "eviron";
export const evSceneUrl = (s: EvScene): string => uiUrl(`banners/banner_${s}.webp`);

export interface EvBannerOpts {
  readonly scene: EvScene;
  readonly title: string;
  /** Chips under the title (timers, counts): already markup. */
  readonly chips?: string;
  /** A picture in a glowing disc between the copy and Bram (the week's event art). */
  readonly art?: string;
  /** A red count on the banner's corner. */
  readonly dot?: number;
  readonly className?: string;
  /** Makes the banner a button (the Events page's live event). */
  readonly tap?: boolean;
}

/** The bright banner (the Shop's `.k-banner.has-scene`, 96 px under `.s3`), its title stroked white on the scene. */
export function evBannerHtml(o: EvBannerOpts): string {
  const tag = o.tap ? "button" : "section";
  return `<${tag}${o.tap ? ` type="button"` : ""} class="k-hero k-banner has-scene evbnr${o.className ? ` ${o.className}` : ""}">`
    + `<img class="k-scene" decoding="async" src="${evSceneUrl(o.scene)}" alt="">`
    + `${o.art ? `<span class="evbart" aria-hidden="true">${o.art}</span>` : ""}`
    + `<div class="k-hero-copy"><b>${o.title}</b>${o.chips ? `<span class="evbchips">${o.chips}</span>` : ""}</div>`
    + `${dotHtml(o.dot ?? 0)}</${tag}>`;
}

/** A chip on a banner (time left, a count): dark glass, white type. */
export function evChipHtml(inner: string, cls = ""): string { return `<span class="evchip${cls ? ` ${cls}` : ""}">${inner}</span>`; }

/** The red dot: a count, or a bare dot for 1 when `bare`. */
export function dotHtml(n: number, bare = false): string {
  return n > 0 ? `<i class="evdot${bare ? " bare" : ""}" aria-label="${n} to claim">${bare ? "" : n}</i>` : "";
}

// ------------------------------------------------------------------ reward parts

/** One thing a reward pays, as a tile shows it. */
export interface RewardPart {
  readonly art: (px: number) => string;
  /** The short name under the well ("Rare chest", "Essence"). */
  readonly name: string;
  /** The count on the well ("60", "+5"), "" for one. */
  readonly count: string;
  /** A speed-up's length on the well's cap. */
  readonly cap?: string;
  /** Rarity square (chests, sigils); null = the neutral blue square. */
  readonly rarity: number | null;
  /** The Shop item for a focus with odds (chests, gems, speed-ups). */
  readonly item?: OfferItem;
}

/** The reward's gold share alone (its words read "1,200 gold" at the Commander's level: eventRewards' resolver). */
const onlyGold = (r: EventReward): EventReward => ({ resources: r.resources, speedUpMinutes: 0, gems: 0, marks: 0, trophies: 0, chest: null, emblem: "", essence: 0, sigil: null, sigils: 0 });

/** A reward's parts, most notable first (as rewardArt picks its one picture). */
export function rewardParts(r: EventReward): RewardPart[] {
  const parts: RewardPart[] = [];
  if (r.emblem) { const id = r.emblem; parts.push({ art: (px) => emblemGlyph(px, id), name: emblemName(id), count: "", rarity: 4 }); }
  if (r.chest !== null) { const c = r.chest; parts.push({ art: (px) => rewardArt({ ...r, emblem: "" }, px), name: `${CHEST_LABEL[c]} chest`, count: "", rarity: c, item: { kind: "chest", rarity: c, count: 1 } }); }
  if (r.sigil !== null) { const s = r.sigil, n = Math.max(1, r.sigils); parts.push({ art: (px) => sigilIconHtml(s, px, "glyph"), name: `${CHEST_LABEL[s]} sigil`, count: n > 1 ? String(n) : "", rarity: s }); }
  if (r.gems > 0) parts.push({ art: (px) => gemGlyph(px), name: "Gems", count: fmtCompact(r.gems), rarity: null, item: { kind: "gems", amount: r.gems, count: 1 } });
  if (r.marks > 0) parts.push({ art: (px) => marksGlyph(px), name: "Marks", count: fmtCompact(r.marks), rarity: null });
  if (r.essence > 0) parts.push({ art: (px) => essenceIconHtml(px, "glyph"), name: "Essence", count: fmtCompact(r.essence), rarity: null });
  if (r.speedUpMinutes > 0) parts.push({ art: () => `<img decoding="sync" src="${resourceIconUrl("speedup")}" alt="">`, name: "Speed-up", count: "", cap: fmtSpeedUp(r.speedUpMinutes), rarity: null, item: { kind: "speedUp", minutes: r.speedUpMinutes, count: 1 } });
  if (r.supply) { const s = r.supply, n = r.supplies ?? 1; parts.push({ art: (px) => supplyIconHtml(s, px), name: supplyName(null, s), count: n > 1 ? String(n) : "", rarity: null }); }
  if ((r.trophies ?? 0) > 0) { const n = r.trophies ?? 0; parts.push({ art: (px) => trophiesGlyph(px), name: n === 1 ? "Trophy" : "Trophies", count: fmtCompact(n), rarity: null }); }
  if (!r.resources.isZero()) {
    const n = /^([\d.,]+[KkM]?) gold$/.exec(rewardWords(onlyGold(r)))?.[1] ?? "";
    parts.push({ art: (px) => `<img decoding="sync" src="${resourceIconUrl("gold")}" alt="" style="height:${px}px">`, name: "Gold", count: n, rarity: null });
  }
  return parts;
}

/** The rewards a tap on a well opens, by key (rebuilt as sheets draw; capped). */
const REWARDS = new Map<string, EventReward>();
let seq = 0;
const CAP = 2000;
function rewardKey(r: EventReward): string {
  if (REWARDS.size >= CAP) REWARDS.clear();
  const k = `r${++seq}`;
  REWARDS.set(k, r);
  return k;
}

/** `data-rw` for a button that opens `r` in focus (bindRewardFocus wires it). */
export function rewardFocusAttr(r: EventReward): string { return `data-rw="${rewardKey(r)}"`; }

/** One part's well (a span: it sits inside a reward's button). */
export function partWellHtml(p: RewardPart, size: "sm" | "md" | "lg", px: number, dim = false, cls = ""): string {
  return wellHtml({ art: p.art(px), rarity: p.rarity, size, count: p.count, cap: p.cap, dim, tag: "span", className: cls });
}

export interface RewardTileOpts {
  /** Art size inside the square (CSS px). */
  readonly px?: number;
  readonly dim?: boolean;
  /** The foot strip (a cost, CLAIM, a tick): sits along the tile's bottom. */
  readonly foot?: string;
  /** A small tag over the tile's top edge (the node's number, "10+"). */
  readonly top?: string;
  readonly className?: string;
  readonly data?: string;
  /** A red dot on the tile's corner. */
  readonly dot?: boolean;
}

/**
 * A reward as a tile: its main part on the rarity square (a tap opens the focus), the part's name under it, "+n" on the
 * square when it pays more than one thing, and the foot strip. Tiles in a row share one height (CSS grid stretch).
 */
export function rewardTileHtml(r: EventReward, o: RewardTileOpts = {}): string {
  const parts = rewardParts(r), main = parts[0];
  if (!main) return "";
  const more = parts.length - 1;
  const tier = main.rarity === null ? "" : ` t-${RARITY_IDS[main.rarity] ?? ""}`;
  return `<div class="evtile${tier}${o.className ? ` ${o.className}` : ""}"${o.data ? ` ${o.data}` : ""}>${o.top ? `<i class="evtop disp">${o.top}</i>` : ""}`
    + `<button type="button" class="evtw" data-rw="${rewardKey(r)}" aria-label="${rewardWords(r)}">${partWellHtml(main, "md", o.px ?? 40, o.dim)}${more > 0 ? `<i class="evmore disp">+${more}</i>` : ""}</button>`
    + `<b class="evtn">${main.name}</b>${o.foot ?? ""}${o.dot ? dotHtml(1, true) : ""}</div>`;
}

/** Every part of a reward in a row of small wells, each with its name (the Trial's prize, a mode's prize). */
export function rewardRowHtml(r: EventReward, px = 30, dim = false): string {
  const key = rewardKey(r);
  return `<div class="evparts">${rewardParts(r).map((p) => `<button type="button" class="evpart" data-rw="${key}">${partWellHtml(p, "sm", px, dim)}<i class="evpn">${p.name}</i></button>`).join("")}</div>`;
}

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" })[c]!);

/**
 * A reward in focus (owner 2026-10-21: a tap on any item): a lone chest, gems or speed-up opens the Shop's own focus
 * (art, what it is, the odds); a mixed reward shows its parts, each named, and the chest's odds when it holds one.
 */
export function showRewardFocus(parent: HTMLElement, B: BalanceData, level: number, r: EventReward): void {
  const parts = rewardParts(r);
  if (parts.length === 0) return;
  if (parts.length === 1 && parts[0].item) { void showItemFocus(parent, B, level, parts[0].item); return; }
  const chest = parts.find((p) => p.item?.kind === "chest")?.item;
  const odds = chest ? oddsHtml(B, chest) : "";
  const list = parts.map((p) => `<span class="evfp">${partWellHtml(p, "sm", 30)}<i>${p.name}</i></span>`).join("");
  const scrim = document.createElement("div");
  scrim.className = `scrim dchooser ${SHOP_SKIN} odds focus evfocus`;
  const title = parts[0].name.toUpperCase();
  scrim.innerHTML = `<div class="panel dialog dchoose" role="dialog" aria-label="${esc(title)}">${ribbonHtml(title, "", "dtitle disp")}${closeBtnHtml()}
    <div class="crows"><div class="ifocus">${partWellHtml(parts[0], "lg", 84, false, "ifwell")}<div class="evfparts">${list}</div>${odds ? `<b class="ifhead">What's inside</b>${odds}` : ""}</div></div></div>`;
  const done = (): void => scrim.remove();
  scrim.querySelector<HTMLButtonElement>(".close")!.onclick = done;
  scrim.onclick = (e) => { if (e.target === scrim) done(); };
  parent.append(scrim);
}

/** Binds every reward under `root` (`[data-rw]`) to its focus. */
export function bindRewardFocus(root: ParentNode, parent: HTMLElement, B: BalanceData, level: number): void {
  for (const b of root.querySelectorAll<HTMLElement>("[data-rw]")) b.onclick = (e) => {
    e.stopPropagation();
    const r = REWARDS.get(b.dataset.rw ?? "");
    if (r) showRewardFocus(parent, B, level, r);
  };
}

/** A tiny glyph + text chip for facts rows (waves, towers, the boss). */
export function factChipHtml(glyph: string, text: string, cls = ""): string { return `<span class="evfact${cls ? ` ${cls}` : ""}">${glyph}${text}</span>`; }
export const clockGlyph = (px = 13): string => icon("clock", px);

/** A section's kicker: its name in the display face and a short note at the end of its line. */
export function evKickHtml(text: string, note = ""): string { return `<div class="evkick">${text}${note ? `<small>${note}</small>` : ""}</div>`; }

/**
 * A chest whose rarity rolls from a band (the Orders' Supply and weekly chests) in focus: its art, what else it pays,
 * and the rarity odds at the Commander's level — the same bars as the Shop's odds.
 */
export function showBandFocus(parent: HTMLElement, B: BalanceData, level: number, band: ChestBand, name: string, art: string, extras: readonly RewardPart[] = []): void {
  const w = bandWeightsAt(B, band, Math.max(1, level)), total = w.reduce((a, b) => a + b, 0) || 1;
  const bars = w.map((x, r) => {
    const p = Math.round((x * 1000) / total) / 10;
    return x > 0 ? `<span class="orow2"><b class="ri-${RARITY_IDS[r]}">${RARITY_IDS[r]}</b><span class="obar"><i style="width:${Math.max(2, p)}%" class="ri-${RARITY_IDS[r]}"></i></span><em>${p}%</em></span>` : "";
  }).join("");
  const list = extras.map((p) => `<span class="evfp">${partWellHtml(p, "sm", 30)}<i>${p.name}</i></span>`).join("");
  const scrim = document.createElement("div");
  scrim.className = `scrim dchooser ${SHOP_SKIN} odds focus evfocus`;
  scrim.innerHTML = `<div class="panel dialog dchoose" role="dialog" aria-label="${esc(name)}">${ribbonHtml(name.toUpperCase(), "", "dtitle disp")}${closeBtnHtml()}
    <div class="crows"><div class="ifocus"><span class="k-well lg ifwell evbandwell">${art}</span>${list ? `<div class="evfparts">${list}</div>` : ""}<b class="ifhead">What's inside</b><div class="dodd2"><span class="obars">${bars}</span></div></div></div></div>`;
  const done = (): void => scrim.remove();
  scrim.querySelector<HTMLButtonElement>(".close")!.onclick = done;
  scrim.onclick = (e) => { if (e.target === scrim) done(); };
  parent.append(scrim);
}
